import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbinResultDto } from '../../../../inge-api/abinclient/models';
import { ButtonGroupItemData } from '../../../../shared-report-components/base-components/fordjupning-button-group/button-group-item.model';
import { ChartCategory, ColorMapService } from '../../../../common/service/color-mapper.service';
import { BehaviorSubject, Subscription } from 'rxjs';
import round from 'lodash-es/round';
import { AndelCombochartBaseComponent } from '../../../../abin-resultat-rapport/base-components/andel-combochart-base/andel-combochart-base.component';
import { Constants } from '../../../../common/constants';
import { GoogleCombochartComponent } from '../../../../google-charts/google-charts-base-components/google-combochart/google-combochart.component';

@Component({
    selector: 'app-kombo-mellanmark',
    templateUrl: './kombo-mellanmark.component.html',
    styleUrl: './kombo-mellanmark.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class KomboMellanmarkComponent extends AndelCombochartBaseComponent implements OnInit, OnDestroy {
    @ViewChild(GoogleCombochartComponent, { static: true })
    googleBarchart: GoogleCombochartComponent;

    private subs: Subscription[] = [];

    // Magra marker diagram
    public dataBarchart: any[][];
    public andelMellanMarker: number = 0;
    public chartName: string = Constants.DIAGRAM_NAMN.TALL_PA_MELLANMARK;

    // Föryngrade trädslag chart
    private chartEntriesTradslag = [
        {
            name: 'Tall',
            key: 'standortsanpassningAndelMellanMarkerMedTall3InvMedel',
            color: ChartCategory.TALL
        },
        {
            name: 'Gran',
            key: 'standortsanpassningAndelMellanMarkerMedGran3InvMedel',
            color: ChartCategory.GRAN
        },
        {
            name: 'Björk',
            key: 'standortsanpassningAndelMellanMarkerMedBjork3InvMedel',
            color: ChartCategory.BJORK
        },
        {
            name: 'Lärk',
            key: 'standortsanpassningAndelMellanMarkerMedLark3InvMedel',
            color: ChartCategory.LARK
        },
        {
            name: 'Contorta',
            key: 'standortsanpassningAndelMellanMarkerMedContorta3InvMedel',
            color: ChartCategory.CONTORTA
        },
        {
            name: 'Övrigt',
            key: 'standortsanpassningAndelMellanMarkerMedOvrigt3InvMedel',
            color: ChartCategory.OVRIG
        },
        {
            name: 'Uppgift saknas',
            key: 'standortsanpassningAndelMellanMarkerMedIngenAtgard3InvMedel',
            color: ChartCategory.INGENATGARD
        }
    ];
    optionsTradslag: any = {
        chartArea: { width: '90%', height: '90%' },
        height: 230,
        pieSliceText: 'none',
        tooltip: {
            trigger: 'none'
        },
        legend: {
            position: 'none'
        }
    };
    dataTradslag: any[][];
    legendDataTradslag: any[];

    private _omradeNamn = new BehaviorSubject<string>(null);
    omradeNamn$ = this._omradeNamn.asObservable();
    private _senasteInventering = new BehaviorSubject<AbinResultDto>(null);
    senasteInventering$ = this._senasteInventering.asObservable();

    ngOnInit(): void {
        this.subs.push(
            this.googleBarchart.chartImg$.subscribe((chartImgObj: { chartName: string; imgUri: string }) => {
                this.chartToImageSvc.setChartImage(chartImgObj);
            })
        );
    }

    ngOnDestroy(): void {
        this.subs.forEach((x) => x.unsubscribe());
    }

    onOmradeSelect(omrade: ButtonGroupItemData) {
        if (omrade && omrade.inventeringar && omrade.inventeringar.length > 0) {
            const inventeringarSorted = omrade.inventeringar.map((a) => a).sort((a, b) => a.invAr - b.invAr);
            const senasteInventering = omrade.inventeringar[0];
            this._omradeNamn.next(omrade.omradeName);
            this.generateChartData(inventeringarSorted);
            this.generateChartDataTradslag(senasteInventering);
            this._senasteInventering.next(senasteInventering);

            // Uppdatera värdet för mellanmark om det finns för inventeringen
            if (senasteInventering.andelMellanMarker3InvMedel && senasteInventering.andelMellanMarker3InvMedel > 0) {
                this.andelMellanMarker = round(senasteInventering.andelMellanMarker3InvMedel * 100, 1);
            }
        }
    }

    generateChartData(inventeringar: AbinResultDto[]) {
        const data: any[][] = [
            [
                'År',
                { id: 'Arsskada_1ar', type: 'number', label: 'Årsvärde' },
                { id: 'Medelfel_low', type: 'number', role: 'interval' },
                { id: 'Medelfel_high', type: 'number', role: 'interval' },
                { id: 'tool_tip_1ar', type: 'string', role: 'tooltip', p: { html: true } },
                { id: 'Medel_3ar', type: 'number', label: 'Sammanvägning för de tre senaste inventeringarna' },
                { id: 'Medelfel_3ar_low', type: 'number', role: 'interval' },
                { id: 'Medelfel_3ar_high', type: 'number', role: 'interval' },
                { id: 'tool_tip_3ar', type: 'string', role: 'tooltip', p: { html: true } }
            ]
        ];

        inventeringar.forEach((inv) => {
            if (inv.standortsanpassningAndelMellanMarkerMedTall || inv.standortsanpassningAndelMellanMarkerMedTall === 0) {
                // Uppdatera värdet för mellanmark om det finns för inventeringen
                if (inv.andelMellanMarker3InvMedel && inv.andelMellanMarker3InvMedel > 0) {
                    this.andelMellanMarker = round(inv.andelMellanMarker3InvMedel * 100, 1);
                }

                // Årsskada
                const arsVarde = round(inv.standortsanpassningAndelMellanMarkerMedTall * 100, 1);
                const mfelArsvarde = inv.medelfelAndelMellanMarkerMedTall * 100 * 2;
                const mfel_Arsvarde_low = arsVarde > 0 ? round(arsVarde - mfelArsvarde, 2) : null;
                const mfel_Arsvarde_high = arsVarde > 0 ? round(arsVarde + mfelArsvarde, 2) : null;

                // 3års medel
                const trearsMedel = round(inv.standortsanpassningAndelMellanMarkerMedTall3InvMedel * 100, 1);
                const mfel3arArsvarde95procent = inv.medelfelAndelMellanMarkerMedTall_3InvMedel * 2 * 100;
                const mfel3arArsvarde_low = trearsMedel - mfel3arArsvarde95procent;
                const mfel3arArsvarde_high = trearsMedel + mfel3arArsvarde95procent;

                data.push([
                    `${inv.invAr}`,
                    arsVarde > 0 ? arsVarde : null,
                    mfel_Arsvarde_low > 0 ? mfel_Arsvarde_low : 0,
                    mfel_Arsvarde_high,
                    this.getTooltip(false, arsVarde, mfelArsvarde, '1.1-1'),
                    trearsMedel,
                    mfel3arArsvarde_low > 0 ? mfel3arArsvarde_low : 0,
                    mfel3arArsvarde_high,
                    this.getTooltip(true, trearsMedel, mfel3arArsvarde95procent, '1.1-1')
                ]);
            }
        });

        this.options = {
            ...this.options,
            tooltip: { isHtml: true },
            interval: {
                Medelfel_low: { style: 'bars', lineWidth: 1 },
                Medelfel_high: { style: 'bars', lineWidth: 1 },
                Medelfel_3ar_low: { style: 'area', curveType: 'none', fillOpacity: 0.48 },
                Medelfel_3ar_high: { style: 'area', curveType: 'none', fillOpacity: 0.48 }
            },
            height: 250,
            vAxis: {
                ...this.options.vAxis,
                maxValue: this.getMaxValue(data)
            }
        };

        this.dataBarchart = data ?? [];
    }

    generateChartDataTradslag(senasteInventering: AbinResultDto) {
        const data: any[][] = [['Markslag', 'Procent']];
        const colors = [];
        const legendData = [];
        for (const entry of this.chartEntriesTradslag) {
            if (senasteInventering[entry.key]) {
                const name = entry.name;
                const value = Math.round(senasteInventering[entry.key] * 100);
                const color = this.colormapService.getColor(entry.color);
                data.push([name, value]);
                colors.push(color);
                legendData.push({ name, value, color });
            }
        }

        this.dataTradslag = data;
        this.optionsTradslag = { ...this.optionsTradslag, colors };
        this.legendDataTradslag = legendData;
    }
}
